<script setup lang="ts">
import type { GlobalCollectionId } from '#layers/data-model';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import { computed, ref } from 'vue';
import { useAppConfig } from '#app/config';
import { useCollection } from '#layers/data-model';
import {
	hasAnyHiddenCollectionFieldPath,
	isCollectionFieldPathPermitted,
	toCollectionFieldPathKey,
} from '#layers/data-model/utils/collection-field-path';
import CollectionFieldPathSelect from '~/components/Collection/CollectionFieldPathSelect.vue';
import { usePermissionsStore } from '~/stores/permissions';
import { tableColumnFieldPathOptions } from './table-column-field-path';

const props = defineProps<{
	collectionId: GlobalCollectionId;
	visibleColumnPaths: CollectionFieldPath[];
}>();

const emit = defineEmits<{
	select: [fieldPath: CollectionFieldPath];
}>();

const appConfig = useAppConfig();
const permissionsStore = usePermissionsStore();

const tooltipDisabled = ref(false);

const { collection } = useCollection(() => props.collectionId);

const visibleColumnKeys = computed(() => new Set(props.visibleColumnPaths.map((fieldPath) => toCollectionFieldPathKey(fieldPath))));

const hasHiddenFields = computed(() => {
	if (!collection.value) return false;
	return hasAnyHiddenCollectionFieldPath(
		collection.value,
		(fieldPath) =>
			isFieldPathPermitted(fieldPath) && !visibleColumnKeys.value.has(toCollectionFieldPathKey(fieldPath)),
		tableColumnFieldPathOptions,
	);
});

function preventTooltip() {
	tooltipDisabled.value = true;
	setTimeout(() => tooltipDisabled.value = false, 2500);
}

function isFieldPathPermitted(fieldPath: CollectionFieldPath) {
	if (!collection.value) return false;

	return isCollectionFieldPathPermitted(collection.value, fieldPath, (collection, field) =>
		permissionsStore.canReadField(collection.id, field));
}

function isAddableFieldPath(fieldPath: CollectionFieldPath) {
	return isFieldPathPermitted(fieldPath) && !visibleColumnKeys.value.has(toCollectionFieldPathKey(fieldPath));
}

function handleSelect(fieldPath: CollectionFieldPath) {
	if (!isAddableFieldPath(fieldPath)) return;

	emit('select', fieldPath);
}
</script>

<template>
	<DTooltip v-if="hasHiddenFields" :text="$t('common.add_field')" :disabled="tooltipDisabled">
		<CollectionFieldPathSelect
			v-if="collection"
			:model-value="null"
			:collection
			:aria-label="$t('common.add_field')"
			v-bind="tableColumnFieldPathOptions"
			:filter-field-path="isAddableFieldPath"
			:search-placeholder="$t('common.search')"
			size="md"
			@update:open="preventTooltip()"
			@select="handleSelect"
		>
			<template #default>
				<DButton
					variant="ghost"
					color="neutral"
					class="size-6 justify-center rounded-sm p-0"
					size="sm"
					:aria-label="$t('common.add_field')"
					:icon="appConfig.ui.icons.plus"
				/>
			</template>
		</CollectionFieldPathSelect>
	</DTooltip>
</template>
